import React from 'react';
import { CalendarClock, AlertTriangle } from 'lucide-react';

// iter316 — Return Visit / More Work Needed capture (non-final Visit outcome).
export const RETURN_REASONS = [
  { key: 'need_parts', label: 'Need parts / materials' },
  { key: 'need_labor', label: 'Need additional labor / more time' },
  { key: 'waiting_approval', label: 'Waiting on estimate / approval' },
  { key: 'waiting_customer', label: 'Waiting on customer' },
  { key: 'waiting_hsp', label: 'Waiting on HSP / warranty' },
  { key: 'need_specialist', label: 'Need another technician / specialty help' },
  { key: 'other', label: 'Other' },
];
const WINDOWS = [
  { key: 'am', label: 'AM' }, { key: 'pm', label: 'PM' },
  { key: 'all_day', label: 'All Day' }, { key: 'specific', label: 'Specific Time' },
];
export const emptyReturnVisit = () => ({
  reason: '', note: '',
  has_suggested: false, suggested: { date: '', window: 'all_day', time: '', note: '' },
  has_promised: false, promised: { date: '', window: 'all_day', time: '', note: '' },
});

const chip = (on) => `text-xs rounded-full border px-3 py-1.5 transition-colors ${on
  ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-white' : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#D4AF37]/60'}`;
const input = 'w-full bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm px-3 py-2';

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2">
    {[false, true].map((v) => (
      <button key={String(v)} type="button" data-testid={`${testid}-${v ? 'yes' : 'no'}`}
        onClick={() => onChange(v)} className={chip(value === v)}>{v ? 'Yes' : 'No'}</button>
    ))}
  </div>
);

const SlotFields = ({ slot, onChange, prefix }) => (
  <div className="space-y-2 mt-2">
    <input type="date" data-testid={`${prefix}-date`} value={slot.date} onChange={(e) => onChange({ ...slot, date: e.target.value })} className={input} />
    <div className="flex flex-wrap gap-2">
      {WINDOWS.map((w) => (
        <button key={w.key} type="button" data-testid={`${prefix}-window-${w.key}`}
          onClick={() => onChange({ ...slot, window: w.key })} className={chip(slot.window === w.key)}>{w.label}</button>
      ))}
    </div>
    {slot.window === 'specific' && (
      <input type="time" data-testid={`${prefix}-time`} value={slot.time} onChange={(e) => onChange({ ...slot, time: e.target.value })} className={input} />
    )}
    <input data-testid={`${prefix}-note`} placeholder="Note (optional)" value={slot.note} onChange={(e) => onChange({ ...slot, note: e.target.value })} className={input} />
  </div>
);

export const returnVisitPayload = (rv) => ({
  reason: rv.reason, note: rv.note,
  suggested: rv.has_suggested && rv.suggested.date ? rv.suggested : null,
  promised: rv.has_promised && rv.promised.date ? rv.promised : null,
});

export const returnVisitError = (rv) => {
  if (!rv.reason) return 'Pick why you need to return';
  for (const k of ['suggested', 'promised']) {
    const on = rv[`has_${k}`];
    if (on && !rv[k].date) return `Pick the ${k} return date (or choose No)`;
    if (on && rv[k].window === 'specific' && !rv[k].time) return `Pick the ${k} return time`;
  }
  return '';
};

export const ReturnVisitFields = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div data-testid="wiz-return-visit" className="space-y-4 rounded-md border border-sky-500/30 bg-sky-500/5 p-3">
      <p className="text-xs text-sky-200 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />
        Today&apos;s Visit is finished. The Job stays open and the office schedules the return. No invoice, estimate or payment today.</p>
      <div>
        <p className="text-xs font-semibold text-white mb-2">Why do you need to return? <span className="text-red-400">*</span></p>
        <div className="flex flex-wrap gap-2">
          {RETURN_REASONS.map((r) => (
            <button key={r.key} type="button" data-testid={`wiz-return-reason-${r.key}`} onClick={() => set({ reason: r.key })} className={chip(value.reason === r.key)}>{r.label}</button>
          ))}
        </div>
        <textarea data-testid="wiz-return-note" rows={2} placeholder="Explain (optional) — e.g. Moen 1225 cartridge on order"
          value={value.note} onChange={(e) => set({ note: e.target.value })} className={`${input} mt-2`} />
      </div>
      <div>
        <p className="text-xs font-semibold text-white mb-2">Do you have a suggested return date?</p>
        <YesNo testid="wiz-return-suggested" value={value.has_suggested} onChange={(v) => set({ has_suggested: v })} />
        {value.has_suggested && <SlotFields prefix="wiz-return-suggested" slot={value.suggested} onChange={(s) => set({ suggested: s })} />}
      </div>
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5">
        <p className="text-xs font-semibold text-amber-200 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Did you promise the customer a return date or time?</p>
        <YesNo testid="wiz-return-promised" value={value.has_promised} onChange={(v) => set({ has_promised: v })} />
        {value.has_promised && <SlotFields prefix="wiz-return-promised" slot={value.promised} onChange={(s) => set({ promised: s })} />}
        {value.has_promised && <p className="text-[10px] text-amber-300/80 mt-2">This does not book the visit. The office sees it as a customer commitment.</p>}
      </div>
    </div>
  );
};
